// src/components/recovery/WeekTabs.tsx
import React, { useRef } from "react";
import { FiCheck, FiLock, FiUnlock } from "react-icons/fi";
import type { WeekGateState as WeekState } from "../../hooks/useWeekUnlock";

interface WeekTabsProps {
  selectedWeek: number;
  onSelectWeek: (week: number) => void;
  weekStates: Record<number, WeekState>;
  totalWeeks?: number;
}

export const WeekTabs: React.FC<WeekTabsProps> = ({
  selectedWeek,
  onSelectWeek,
  weekStates,
  totalWeeks = 6,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  // FIX: Removed unused 'hasLockedWeek' variable

  const getTabClasses = (week: number): string => {
    const state = weekStates[week] ?? "locked";
    const isSelected = week === selectedWeek;
    const base = "flex items-center justify-center gap-1 px-3 py-2.5 rounded-full border-none cursor-pointer whitespace-nowrap flex-shrink-0 transition-all duration-200 font-dm-sans text-sm font-medium";

    if (state === "completed") {
      if (isSelected) return `${base} ring-2 ring-brand-primary ring-offset-2 bg-brand-primary text-white`;
      return `${base} bg-brand-primary text-white`;
    }
    if (state === "active") {
      if (isSelected) return `${base} bg-brand-light text-brand-primary`;
      return `${base} bg-gray-100 text-gray-500 hover:bg-gray-200`;
    }
    if (state === "unlockable") {
      return `${base} bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200`;
    }
    return `${base} bg-gray-100 text-gray-400 cursor-not-allowed`;
  };

  return (
    <div className="mt-4 overflow-hidden">
      <div
        ref={scrollRef}
        className="flex gap-2 px-6 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {weeks.map((week) => {
          const state = weekStates[week] ?? "locked";
          const isLocked = state === "locked";
          const isCompleted = state === "completed";
          const isUnlockable = state === "unlockable";

          return (
            <button
              key={week}
              type="button"
              onClick={() => !isLocked && onSelectWeek(week)}
              disabled={isLocked}
              className={getTabClasses(week)}
              aria-label={`Week ${week}${isLocked ? " (locked)" : isCompleted ? " (completed)" : isUnlockable ? " (ready to unlock)" : ""}`}
            >
              {isCompleted && <FiCheck size={16} />}
              {isUnlockable && <FiUnlock size={14} />}
              {isLocked && <FiLock size={14} />}
              <span>Week {week}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WeekTabs;